/**
 * 1.7.4 — who holds a place at an event, and how the raffle gives them (D-1.7.4-1…4). Pure rules shared by the API
 * and the app; nothing here reads or writes a list.
 *
 * - Two roles: a tenant (with the guests they announce, and in a raffle the one extra person from their household)
 *   and a volunteer. A volunteer is never counted as a tenant and has places of their own.
 * - The numbers of an event: Guests allowed (0 / 1 / 2), Tenant places, Volunteer places, Raffle on / off,
 *   Household extra ticket on / off. Changeable at any time; each change says what it means.
 * - The draw: the people marked Guaranteed (and the ones the office added by hand as winners) are seated first and
 *   never drawn; everyone else is put in an order by a shuffle whose random numbers come from the platform's
 *   cryptographic source — the caller passes it in, so nothing here can pick an order by a pattern. A household with
 *   an extra ticket is one unit: both or neither.
 * - The three counts of the event's page, and the one-line list of addresses for Outlook's BCC.
 */
import { z } from 'zod';

export type RsvpRole = 'tenant' | 'volunteer';
export const roleOfType = (personType: string): RsvpRole => (personType === 'Individual Volunteer' || personType === 'Group Volunteer' ? 'volunteer' : 'tenant');

/** Where a person stands on the event. `going` is a place on an event with no draw; `inDraw` is a raffle before its draw. */
export const PLACE_STATUSES = ['guaranteed', 'winner', 'going', 'inDraw', 'waiting', 'no', 'noAnswer', 'cancelled'] as const;
export type PlaceStatus = (typeof PLACE_STATUSES)[number];
/** The statuses that hold a place. */
export const HOLDS: readonly PlaceStatus[] = ['guaranteed', 'winner', 'going'];

export interface PlaceRow {
  role: RsvpRole;
  coming: string;
  standby: boolean;
  raffleOutcome: string;
  guaranteed: boolean;
  cancelled: boolean;
}

export function placeStatus(r: PlaceRow, ctx: { raffle: boolean; drawn: boolean; volunteersDrawn: boolean }): PlaceStatus {
  if (r.cancelled) return 'cancelled';
  if (r.coming === 'No Answer' || r.coming === '') return 'noAnswer';
  if (r.coming !== 'Yes') return 'no';
  if (r.guaranteed) return 'guaranteed';
  if (r.raffleOutcome === 'Winner') return 'winner';
  if (r.standby || r.raffleOutcome === 'Standby') return 'waiting';
  if (ctx.raffle && r.role === 'tenant') return ctx.drawn ? 'waiting' : 'inDraw';
  return ctx.raffle && r.role === 'volunteer' && ctx.volunteersDrawn ? 'waiting' : 'going';
}

/* ------------------------------------------------------------------------------------------------
 * The numbers
 * ---------------------------------------------------------------------------------------------- */

export const GUESTS_ALLOWED = [0, 1, 2] as const;
export type GuestsAllowed = (typeof GUESTS_ALLOWED)[number];
export const GUESTS_ALLOWED_DEFAULT: GuestsAllowed = 2;
/** Settings → Events and RSVPs: what a new event starts with. */
export const EVENT_GUESTS_ALLOWED = 'EVENT_GUESTS_ALLOWED';
export const guestsAllowedOf = (v: unknown): GuestsAllowed => (String(v ?? '').trim() === '0' ? 0 : String(v ?? '').trim() === '1' ? 1 : String(v ?? '').trim() === '2' ? 2 : GUESTS_ALLOWED_DEFAULT);

export interface EventNumbers {
  guestsAllowed: GuestsAllowed;
  /** The cap for tenants and the people they bring; null = no limit. */
  tenantPlaces: number | null;
  /**
   * How many volunteers are needed (the Volunteers card's "Volunteers needed"). 17 Oct 2026, the plan of record: null
   * (empty) = no volunteers needed — nobody is asked; a volunteer invited or added anyway holds a place, with no limit,
   * and is still shown (the Volunteers card: "How many? Not set"). 0 = no places set aside — a new yes waits.
   */
  volunteerPlaces: number | null;
  raffle: boolean;
  /** Raffle only: the RSVP asks whether one more person from the household would like a ticket. */
  householdExtra: boolean;
  /** A volunteer may bring guests (up to Guests allowed). Off unless the office turns it on. */
  volunteerGuests: boolean;
}

const places = z.number().int().min(0).max(5000);
export const NumbersSchema = z
  .object({
    guestsAllowed: z.union([z.literal(0), z.literal(1), z.literal(2)]).optional(),
    tenantPlaces: places.nullable().optional(),
    volunteerPlaces: places.nullable().optional(),
    raffle: z.boolean().optional(),
    householdExtra: z.boolean().optional(),
    volunteerGuests: z.boolean().optional(),
  })
  .strict();
export type NumbersInput = z.input<typeof NumbersSchema>;
export type NumbersField = keyof EventNumbers;

export interface NumbersChange {
  at: string;
  by: string;
  field: NumbersField;
  from: number | boolean | null;
  to: number | boolean | null;
  /** "Tenant places 20 → 22: two more will be drawn." — shown on the page as written. */
  meaning: string;
}

const FIELD_WORDS: Record<NumbersField, string> = {
  guestsAllowed: 'Guests allowed',
  tenantPlaces: 'Tenant places',
  volunteerPlaces: 'Volunteer places',
  raffle: 'Raffle',
  householdExtra: 'Household extra ticket',
  volunteerGuests: 'Guests of volunteers',
};
const SMALL = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
const inWords = (n: number) => SMALL[n] ?? String(n);
// 17 Oct 2026: an empty Volunteer places is "not set" (no volunteers needed) — an empty Tenant places is "no limit"
const shown = (v: number | boolean | null, field?: NumbersField) => (v === null ? (field === 'volunteerPlaces' ? 'not set' : 'no limit') : v === true ? 'on' : v === false ? 'off' : String(v));

/** What a changed number means, in one sentence. */
export function numbersMeaning(field: NumbersField, from: number | boolean | null, to: number | boolean | null, ctx: { raffle: boolean; drawn: boolean }): string {
  const head = `${FIELD_WORDS[field]} ${shown(from, field)} → ${shown(to, field)}: `;
  if (field === 'volunteerPlaces' && (to === null || from === null)) {
    // 17 Oct 2026, the plan of record: empty = no volunteers needed (nobody is asked; whoever said yes keeps a place)
    if (to === null) return `${head}no volunteers needed now — nobody more is asked; everyone who already said yes has a place.`;
    const n = Number(to);
    return n > 0
      ? `${head}${inWords(n)} ${n === 1 ? 'volunteer' : 'volunteers'} needed now — nobody who has a place loses it; past the number a new yes waits.`
      : `${head}no places set aside now — nobody who has a place loses it; a new yes waits.`;
  }
  if (field === 'tenantPlaces' || field === 'volunteerPlaces') {
    const who = field === 'tenantPlaces' ? '' : ' volunteers';
    if (to === null) return `${head}no limit now — everyone who says yes has a place.`;
    if (from === null) return `${head}a limit now — nobody who has a place loses it; past it a new yes waits.`;
    const d = Number(to) - Number(from);
    const n = inWords(Math.abs(d));
    if (d > 0) {
      if (ctx.raffle && field === 'tenantPlaces') return ctx.drawn ? `${head}${n} more can be offered to the next on the waiting list — press Offer to the next.` : `${head}${n} more will be drawn.`;
      // 1.8.3: a volunteer on the standby list does not move up by itself — the office offers the place
      if (field === 'volunteerPlaces') return `${head}${n} more ${d === 1 ? 'volunteer' : 'volunteers'} can have a place — a new yes takes one; for someone on the standby list, press Offer to the next on standby.`;
      return `${head}${n} more${who} can have a place; the first waiting take them.`;
    }
    if (ctx.raffle && field === 'tenantPlaces' && !ctx.drawn) return `${head}${n} fewer will be drawn.`;
    return `${head}${n} fewer — nobody loses a place they already have; the page says when more are going than there are places.`;
  }
  if (field === 'guestsAllowed') return `${head}${to === 0 ? 'the RSVP no longer asks about guests' : `a tenant may announce up to ${inWords(Number(to))}`}. Answers already given are kept as they are.`;
  if (field === 'raffle') return `${head}${to ? 'places are given by a draw — one ticket per tenant, no guests.' : 'places are given in the order people answer.'}`;
  if (field === 'householdExtra') return `${head}${to ? 'the RSVP asks whether one more person from the household would like a ticket; the two enter the draw as one.' : 'the RSVP no longer asks; extras already given are kept.'}`;
  return `${head}${to ? 'a volunteer may announce guests, like a tenant.' : 'a volunteer answers for themselves only.'}`;
}

/* ------------------------------------------------------------------------------------------------
 * The draw
 * ---------------------------------------------------------------------------------------------- */

/** One entry in the pool: a tenant (1) or a tenant with the household extra (2) — both or neither. */
export interface DrawUnit {
  id: string;
  people: number;
}

/**
 * Fisher–Yates. `randomInt(n)` must give a whole number from 0 up to n − 1; the API passes the platform's
 * cryptographic source (`crypto.randomInt`). Nothing is chosen by a person and there is no way to ask for the same order twice.
 */
export function shuffled<T>(items: readonly T[], randomInt: (n: number) => number): T[] {
  const a = [...items];
  for (let i = a.length - 1; i > 0; i--) {
    const j = randomInt(i + 1);
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

/**
 * Who is drawn. The places already taken by the people seated first (Guaranteed, winners by hand) come off the
 * top; then, in the drawn order, each unit takes its places when they all fit — a pair that does not fit waits
 * (ahead of everyone after it) and a single person behind may take the place that is left.
 */
export function settleDraw(order: readonly DrawUnit[], placesTotal: number | null, seatedFirst: number): { winners: string[]; waiting: string[] } {
  let left = placesTotal === null ? Number.MAX_SAFE_INTEGER : Math.max(0, placesTotal - Math.max(0, seatedFirst));
  const winners: string[] = [];
  const waiting: string[] = [];
  for (const u of order) {
    const need = Math.max(1, u.people);
    if (need <= left) {
      left -= need;
      winners.push(u.id);
    } else waiting.push(u.id);
  }
  return { winners, waiting };
}

/** The request: nothing to choose. Drawing a second time is the administrator's, with a reason. */
export const DrawAgainSchema = z
  .object({
    again: z.boolean().optional().default(false),
    reason: z.string().trim().max(200).optional().default(''),
  })
  .strict()
  .refine((d) => !d.again || d.reason.length >= 3, {
    message: 'required',
    path: ['reason'],
  });
export type DrawInput = z.input<typeof DrawAgainSchema>;

/** One draw, as it is kept: the evidence, not a way to run it again. */
export interface DrawRecord {
  drawDateTime: string;
  kind: 'tenants' | 'volunteers';
  /** The places the draw was for (Tenant places, or Volunteer places). */
  places: number | null;
  /** People in the pool. */
  poolSize: number;
  /** The exact pool, in a fixed order (by id). */
  pool: DrawUnit[];
  /** The order the draw put the pool in. */
  order: string[];
  /** Seated first, never drawn. */
  guaranteed: string[];
  winners: string[];
  standby: string[];
  runBy: string;
  /** SHA-256 of the pool, the order and the time. */
  fingerprint: string;
  again: { reason: string } | null;
  /** Worked out when read: the kept record still gives this fingerprint. */
  verified?: boolean;
}

/* ------------------------------------------------------------------------------------------------
 * The office's hand
 * ---------------------------------------------------------------------------------------------- */

const reason = z.string().trim().min(3, 'required').max(120);
const rsvpId = z.string().trim().min(5).max(40);

export const GuaranteeSchema = z
  .object({
    rsvpId,
    on: z.boolean(),
    reason: z.string().trim().max(120).optional().default(''),
  })
  .refine((g) => !g.on || g.reason.length >= 3, {
    message: 'required',
    path: ['reason'],
  });
export type GuaranteeInput = z.input<typeof GuaranteeSchema>;

export const CancelSchema = z.object({ rsvpId, reason });
export type CancelInput = z.input<typeof CancelSchema>;

export const BY_HAND_STATES = ['guaranteed', 'inDraw', 'winner', 'waiting'] as const;
export type ByHandState = (typeof BY_HAND_STATES)[number];
export const AddByHandSchema = z.object({
  person: z.discriminatedUnion('type', [
    z.object({
      type: z.literal('tenant'),
      tenantId: z.string().trim().min(3).max(50),
    }),
    z.object({
      type: z.literal('volunteer'),
      volunteerId: z.string().trim().min(3).max(50),
    }),
    /** 1 Nov 2026 (D-1102-STAFF): a staff member or colleague volunteering at one event — not in SM_Volunteers; phone and email optional. */
    z.object({
      type: z.literal('staff'),
      name: z.string().trim().min(1, 'required').max(120),
      phone: z.string().trim().max(40).optional().default(''),
      email: z.union([z.literal(''), z.string().trim().toLowerCase().max(254).regex(/^[^@\s]+@[^@\s]+\.[^@\s]+$/, 'email')]).optional().default(''),
    }),
  ]),
  as: z.enum(BY_HAND_STATES),
  reason,
  /** Raffle with the household extra on: the one more person from the household comes too. */
  householdExtra: z.boolean().optional().default(false),
  /** 25 Oct 2026: how many of the guests need a wheelchair spot (the office's pre-pass flag). */
  wheelchairGuests: z.number().int().min(0).max(10).optional(),
  /** Eli, 17 Oct 2026: a tenant added by the office on an event without a draw may bring guests (up to Guests allowed). */
  guests: z.number().int().min(0).max(2).optional(),
});
export type AddByHandInput = z.input<typeof AddByHandSchema>;

export const OfferNextSchema = z.object({
  role: z.enum(['tenant', 'volunteer']).optional().default('tenant'),
});
export type OfferNextInput = z.input<typeof OfferNextSchema>;

/** 1.7.5 — the office sets or clears Needs delivery on a line, with a reason (the bedbound tenant who answered "come down" by habit). */
export const DeliverySchema = z.object({ rsvpId, on: z.boolean(), reason });
export type DeliveryInput = z.input<typeof DeliverySchema>;

/** 1.7.5 — "food or items served" on an event, and what ("what we serve"). Changeable at any time. */
export const ServedSchema = z.object({ on: z.boolean(), what: z.string().trim().max(200).optional().default('') });
export type ServedInput = z.input<typeof ServedSchema>;

/** 1.7.5 — the four papers of an event. */
export const EVENT_PRINTS = ['attendance', 'roster', 'delivery', 'report'] as const;
export type EventPrint = (typeof EVENT_PRINTS)[number];

/** A mark the office put on a line: why, who, when. */
export interface HandMark {
  reason: string;
  by: string;
  at: string;
}

/* ------------------------------------------------------------------------------------------------
 * The three counts
 * ---------------------------------------------------------------------------------------------- */

export interface ThreeRow {
  role: RsvpRole;
  status: PlaceStatus;
  /** Everyone this line brings: the person, the household, the guests. */
  people: number;
  guests: number;
  householdExtra: boolean;
  /** Waiting for the office to say whose answer it is. */
  review: boolean;
  /** 1.7.5 — their plate goes upstairs. With no raffle they take no seat: the places are counted without them. */
  upstairs?: boolean;
}

/** 1.7.5 — "22 yes · 3 of them upstairs · 19 seats", in people: yes for what is served, no for a seat. */
export interface PlateCounts {
  yes: number;
  upstairs: number;
  seats: number;
}

export interface ThreeCounts {
  tenants: {
    answered: number;
    yes: number;
    householdExtras: number;
    guests: number;
  };
  volunteers: { answered: number; yes: number };
  /** All in people. */
  places: {
    tenantPlaces: number | null;
    guaranteed: number;
    winners: number;
    waiting: number;
    /** A raffle before its draw: people in the pool. */
    inDraw: number;
    /** The last tenants' draw: people it was drawn from; null when none. */
    drawnFrom: number | null;
    left: number | null;
    /** More going than there are places ("22 going for 20 places — you said so"). */
    over: number;
    /** Places that can be offered to the waiting list by a press (a raffle after its draw). */
    open: number;
    volunteerPlaces: number | null;
    volunteersGoing: number;
    volunteersWaiting: number;
    volunteersLeft: number | null;
    volunteersOpen: number;
    /** 1.7.5 — people whose plate goes upstairs (they hold no seat when the event has no raffle). */
    upstairs: number;
  };
}

export function threeCounts(rows: readonly ThreeRow[], n: Pick<EventNumbers, 'tenantPlaces' | 'volunteerPlaces' | 'raffle'>, ctx: { drawn: boolean; volunteersDrawn: boolean; drawnFrom: number | null }): ThreeCounts {
  const sum = (xs: readonly ThreeRow[]) => xs.reduce((s, r) => s + Math.max(1, r.people), 0);
  // 1.7.5: a plate that goes upstairs is a yes and not a seat — with no raffle the places are counted without it
  // (a raffle gives tickets: a winner upstairs still holds one, and the draw is not touched)
  const noSeat = (r: ThreeRow) => !!r.upstairs && !n.raffle;
  const all = rows.filter((r) => r.role === 'tenant');
  const t = all.filter((r) => !noSeat(r));
  const v = rows.filter((r) => r.role === 'volunteer');
  const said = (r: ThreeRow) => r.status !== 'noAnswer';
  const yes = (r: ThreeRow) => r.status !== 'noAnswer' && r.status !== 'no' && r.status !== 'cancelled';
  const guaranteed = sum(t.filter((r) => r.status === 'guaranteed'));
  const winners = sum(t.filter((r) => r.status === 'winner' || r.status === 'going'));
  const waiting = sum(t.filter((r) => r.status === 'waiting'));
  const taken = guaranteed + winners;
  const left = n.tenantPlaces === null ? null : Math.max(0, n.tenantPlaces - taken);
  const vGoing = sum(v.filter((r) => HOLDS.includes(r.status)));
  const vWaiting = sum(v.filter((r) => r.status === 'waiting'));
  const vLeft = n.volunteerPlaces === null ? null : Math.max(0, n.volunteerPlaces - vGoing);
  return {
    tenants: {
      answered: all.filter(said).length,
      yes: all.filter(yes).length,
      householdExtras: all.filter((r) => yes(r) && r.householdExtra).length,
      guests: all.filter(yes).reduce((s, r) => s + r.guests, 0),
    },
    volunteers: { answered: v.filter(said).length, yes: v.filter(yes).length },
    places: {
      tenantPlaces: n.tenantPlaces,
      guaranteed,
      winners,
      waiting,
      inDraw: sum(t.filter((r) => r.status === 'inDraw')),
      drawnFrom: ctx.drawnFrom,
      left,
      over: n.tenantPlaces === null ? 0 : Math.max(0, taken - n.tenantPlaces),
      open: n.raffle && ctx.drawn && left !== null && waiting > 0 ? left : 0,
      volunteerPlaces: n.volunteerPlaces,
      volunteersGoing: vGoing,
      volunteersWaiting: vWaiting,
      volunteersLeft: vLeft,
      volunteersOpen: n.raffle && ctx.volunteersDrawn && vLeft !== null && vWaiting > 0 ? vLeft : 0,
      upstairs: sum(rows.filter((r) => !!r.upstairs && (HOLDS.includes(r.status) || r.status === 'inDraw'))),
    },
  };
}

/* ------------------------------------------------------------------------------------------------
 * Copy addresses
 * ---------------------------------------------------------------------------------------------- */

const ADDRESS = /^[^@\s;,]+@[^@\s;,]+\.[^@\s;,]+$/;

/** The recipients' addresses on one line, semicolon-separated, each once — what Outlook's BCC box takes. */
export function bccLine(addresses: readonly string[]): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of addresses) {
    const a = raw.trim();
    if (!ADDRESS.test(a) || seen.has(a.toLowerCase())) continue;
    seen.add(a.toLowerCase());
    out.push(a);
  }
  return out.join('; ');
}

export interface AddressesResult {
  who: 'tenants' | 'volunteers';
  line: string;
  count: number;
}

/** The words of a status and a role in the export (English, like every sheet the office keeps). */
export const STATUS_WORDS: Record<PlaceStatus, string> = {
  guaranteed: 'Guaranteed',
  winner: 'Winner',
  going: 'Going',
  inDraw: 'In the draw',
  waiting: 'Waiting',
  no: 'No',
  noAnswer: 'No answer',
  cancelled: 'Cancelled',
};
export const ROLE_WORDS: Record<RsvpRole, string> = {
  tenant: 'Tenant',
  volunteer: 'Volunteer',
};
